/**
 * NIRSALContext — Single source of truth for NIRSAL workspace.
 * Fully isolated from NB, Dangote, and Oko-Oba workspaces.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import client from "@/api/client";

const Ctx = createContext(null);
export const useNIRSAL = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useNIRSAL must be used within NIRSALProvider");
  return ctx;
};

export function NIRSALProvider({ children }) {
  const [periods,        setPeriods]        = useState([]);
  const [selectedPeriod, setSelectedPeriodState] = useState(null);
  const [consolidated,   setConsolidated]   = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [vbCache,        setVbCache]        = useState({});
  const [vbLoading,      setVbLoading]      = useState(false);

  useEffect(() => {
    client.get("/nirsal/periods").then(r => {
      const ps = r.data || [];
      setPeriods(ps);
      const active =
        ps.find(p => p.status === "approved") ||
        ps.find(p => p.status === "submitted") ||
        ps.find(p => p.status === "draft") ||
        ps[0];
      if (active) setSelectedPeriodState(active.period_id);
    }).catch(() => setLoading(false));
  }, []);

  const loadConsolidated = useCallback((pid) => {
    if (!pid) return;
    setLoading(true);
    client.get(`/nirsal/consolidated/${pid}`)
      .then(r => { setConsolidated(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { if (selectedPeriod) loadConsolidated(selectedPeriod); }, [selectedPeriod, loadConsolidated]);

  const period = useMemo(() => periods.find(p => p.period_id === selectedPeriod) || null, [periods, selectedPeriod]);

  const setSelectedPeriod = useCallback((pid) => {
    setSelectedPeriodState(pid);
    setVbCache(prev => { const n = { ...prev }; delete n[pid]; return n; });
  }, []);

  const loadVb = useCallback((pid) => {
    if (!pid || vbCache[pid]) return;
    setVbLoading(true);
    client.get(`/nirsal/value-bridge/${pid}`)
      .then(r => { setVbCache(prev => ({ ...prev, [pid]: r.data })); setVbLoading(false); })
      .catch(() => setVbLoading(false));
  }, [vbCache]);

  useEffect(() => { if (selectedPeriod) loadVb(selectedPeriod); }, [selectedPeriod, loadVb]);

  const invalidateVb = useCallback((pid) => {
    const p = pid || selectedPeriod;
    if (!p) return;
    client.get(`/nirsal/value-bridge/${p}`)
      .then(r => setVbCache(prev => ({ ...prev, [p]: r.data })))
      .catch(() => {});
  }, [selectedPeriod]);

  const refresh = useCallback(() => { if (selectedPeriod) loadConsolidated(selectedPeriod); }, [selectedPeriod, loadConsolidated]);

  return (
    <Ctx.Provider value={{
      periods, selectedPeriod, setSelectedPeriod, period,
      consolidated, loading, refresh,
      vbCache, vbLoading, invalidateVb,
      evidencePct: consolidated?.evidence_completeness_pct ?? 0,
    }}>
      {children}
    </Ctx.Provider>
  );
}
